import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardActionArea, 
  useTheme, 
  CircularProgress, 
  Alert, 
  Chip,
  Paper,
  Container,
  alpha,
  Skeleton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useThemeMode } from '../App';

const API_URL = 'https://guide.zerdegroup.kz/api/';

interface Article {
  id: number;
  title: string;
  content: string;
  category_id: number;
  subcategory_id: number;
  author_id: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  author: {
    id: number;
    login: string;
    role: string;
  };
  category: {
    id: number;
    title: string;
  };
  subcategory: {
    id: number;
    title: string;
  };
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('ru-RU', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  }).format(date);
};

const Home: React.FC = () => {
  const theme = useTheme();
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // --- Советы дня для конечных пользователей ---
  const tips = [
    'Используйте поиск для быстрого доступа к нужной инструкции.',
    'Добавляйте статьи в "Избранное" для быстрого доступа к ним в будущем.',
    'Проверьте дату обновления статьи — используйте только актуальные инструкции.',
    'Если инструкция не помогла — обратитесь к автору статьи.',
    'В каждой категории есть подразделы — используйте их для уточнения поиска.',
    'Обновите страницу, если что-то отображается некорректно.',
    'Воспользуйтесь фильтрами по категориям для быстрого поиска нужной информации.',
    'Сохраняйте важные инструкции в закладки браузера.',
    'Если нашли ошибку в статье — сообщите об автору.'
  ];
  const [tip, setTip] = useState('');
  useEffect(() => {
    setTip(tips[Math.floor(Math.random() * tips.length)]);
  }, []);

  // Функция для очистки текста от Markdown и получения первых строк
  const getCleanPreview = (content: string, maxLength: number = 150): string => {
    // Удаляем заголовки
    let cleanText = content.replace(/^#+\s/gm, '');
    
    // Удаляем жирный и курсивный текст
    cleanText = cleanText.replace(/\*\*|\*|__|_/g, '');
    
    // Удаляем код
    cleanText = cleanText.replace(/`{1,3}[^`]*`{1,3}/g, '');
    
    // Удаляем ссылки, оставляя только текст
    cleanText = cleanText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    
    // Удаляем списки
    cleanText = cleanText.replace(/^\s*[-*+]\s/gm, '');
    cleanText = cleanText.replace(/^\s*\d+\.\s/gm, '');
    
    // Удаляем пустые строки и лишние пробелы
    cleanText = cleanText.replace(/\n\s*\n/g, '\n').trim();
    
    // Получаем первые строки
    const lines = cleanText.split('\n').slice(0, 3);
    
    // Объединяем строки и обрезаем до maxLength
    let preview = lines.join(' ').trim();
    if (preview.length > maxLength) {
      preview = preview.substring(0, maxLength) + '...';
    }
    
    return preview;
  };

  useEffect(() => {
    const fetchLatestArticles = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await fetch(`${API_URL}/articles/latest/15`);
        
        if (!response.ok) {
          throw new Error('Ошибка при загрузке статей');
        }

        const data = await response.json();
        setArticles(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Произошла ошибка');
      } finally {
        setLoading(false);
      }
    };

    fetchLatestArticles();
  }, []);

  useEffect(() => {
    // Больше не нужен кастомный курсор
  }, []);

  const getGradientBackground = () => {
    return mode === 'light'
      ? `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
      : `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`;
  };

  return (
    <Container className="fade-in" maxWidth="lg" sx={{ py: 2, position: 'relative' }}>
      {/* Глобальные стили для анимаций и глитча */}
      <style>{`
        @keyframes animatedGradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .wave {
          animation: waveMove 30s linear infinite;
          transform-origin: center;
        }
        @keyframes waveMove {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .wave-second {
          animation: waveMove 24s linear infinite reverse;
        }
        .decor-ellipse {
          opacity: 0.06;
          transition: opacity 0.3s;
        }
        .decor-circle {
          opacity: 0.04;
          transition: opacity 0.3s;
        }
        @media (max-width: 600px) {
          .wave {
            transform-origin: bottom;
            animation-duration: 36s;
          }
          .wave-second {
            animation-duration: 30s;
          }
        }
      `}</style>
      <Paper
        elevation={0}
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          mb: 6,
          background: getGradientBackground(),
          backgroundSize: '400% 400%',
          animation: 'animatedGradient 15s ease-in-out infinite',
          color: 'white',
          py: { xs: 6, md: 10 },
          px: 3,
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* SVG-фигуры и волны */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          pointerEvents: 'none',
        }}>
          {/* Волна */}
          <svg width="200%" height="100%" viewBox="0 0 1600 300" preserveAspectRatio="xMinYMin slice" style={{ position: 'absolute', bottom: 0, left: 0 }}>
            <path className="wave" d="M0 200 Q200 250 400 200 Q600 150 800 200 Q1000 250 1200 200 Q1400 150 1600 200 V300 H0 Z" fill={theme.palette.primary.light} fillOpacity="0.08" />
            <path className="wave wave-second" d="M0 220 Q200 270 400 220 Q600 180 800 220 Q1000 260 1200 220 Q1400 180 1600 220 V300 H0 Z" fill={theme.palette.secondary.light} fillOpacity="0.07" />
            <ellipse className="decor-ellipse" cx="15%" cy="30%" rx="80" ry="40" fill={theme.palette.primary.main} />
            <ellipse className="decor-ellipse" cx="85%" cy="70%" rx="60" ry="30" fill={theme.palette.secondary.main} />
            <circle className="decor-circle" cx="50%" cy="50%" r="50" fill={theme.palette.primary.dark} />
          </svg>
        </Box>
        <Typography 
          variant="h2" 
          component="h1" 
          gutterBottom
          sx={{ 
            fontWeight: 800,
            mb: 3,
            textShadow: '0 2px 8px rgba(0,0,0,0.18)',
            fontSize: { xs: '2.2rem', sm: '3rem', md: '3.5rem' },
            zIndex: 1,
            position: 'relative',
          }}
        >
          Zerde Guide
        </Typography>
        <Typography 
          variant="h5"
          sx={{ 
            maxWidth: 700,
            textShadow: '0 1px 2px rgba(0,0,0,0.1)',
            fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' },
            fontWeight: 400,
            lineHeight: 1.5,
            zIndex: 1,
          }}
        >
          База знаний и документация по разработке
        </Typography>
        {/* Плашка дня */}
        <Box sx={{
          mt: 3,
          mb: -2,
          px: 3,
          py: 1.5,
          borderRadius: 3,
          background: alpha(theme.palette.background.paper, 0.18),

          fontWeight: 600,
          fontSize: { xs: '1rem', md: '1.15rem' },
          boxShadow: '0 2px 12px 0 rgba(0,0,0,0.07)',
          zIndex: 2,
          position: 'relative',
          display: 'inline-block',
        }}>
          💡 {tip}
        </Box>
      </Paper>
      <Typography 
        variant="h4" 
        component="h2" 
        gutterBottom
        sx={{ 
          fontWeight: 700, 
          mb: 4,
          px: 1,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -8,
            left: 0,
            width: 60,
            height: 4,
            borderRadius: 2,
            backgroundColor: 'primary.main',
          }
        }}
      >
        Последние статьи
      </Typography>
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Grid container spacing={3}>
            {[...Array(6)].map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="100%" height={32} sx={{ mb: 1.5 }} />
                    <Skeleton variant="text" width="100%" height={20} />
                    <Skeleton variant="text" width="90%" height={20} />
                    <Skeleton variant="text" width="95%" height={20} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={3}>
            {articles.map((article) => {
              // Определяем, новая ли статья (3 дня)
              const isNew = (Date.now() - new Date(article.created_at).getTime()) < 1000 * 60 * 60 * 24 * 3;
              return (
                <Grid item xs={12} sm={6} md={4} key={article.id}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      borderRadius: 1.5,
                      overflow: 'hidden',
                      border: `1px solid ${alpha(theme.palette.divider, 0.25)}`,
                      background: mode === 'light'
                        ? alpha(theme.palette.background.paper, 0.98)
                        : alpha(theme.palette.background.paper, 0.90),
                      boxShadow: 'none',
                      transition: 'all 0.28s cubic-bezier(.23,1.02,.64,.98)',
                      position: 'relative',
                      p: 0,
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        background: theme.palette.primary.main,
                        opacity: 0.7,
                        borderTopLeftRadius: 6,
                        borderBottomLeftRadius: 6,
                        zIndex: 1,
                      },
                      '&:hover': {
                        transform: 'translateY(-7px) scale(1.025)',
                        boxShadow: mode === 'light' 
                          ? '0 8px 32px rgba(0,0,0,0.13)' 
                          : '0 8px 32px rgba(0,0,0,0.33)',
                        borderColor: theme.palette.primary.main,
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: '2px',
                      },
                    }}
                  >
                    <CardActionArea 
                      onClick={() => navigate(`/article/${article.category.id}/${article.subcategory.id}/${article.id}`)}
                      sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', p: 0, position: 'relative', zIndex: 2 }}
                    >
                      <CardContent sx={{ flex: 1, width: '100%', p: 1.7, pb: 1.1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Box sx={{ display: 'flex', gap: 0.7, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Chip 
                            size="small" 
                            label={article.category.title}
                            sx={{ 
                              backgroundColor: alpha(theme.palette.primary.main, 0.10),
                              color: 'primary.main',
                              fontWeight: 500,
                              fontSize: '0.88rem',
                              letterSpacing: 0.1,
                              px: 0.8,
                              height: 22,
                              borderRadius: 1.5,
                            }}
                          />
                          <Chip 
                            size="small" 
                            label={article.subcategory.title}
                            sx={{ 
                              backgroundColor: alpha(theme.palette.secondary.main, 0.10),
                              color: 'secondary.main',
                              fontWeight: 500,
                              fontSize: '0.88rem',
                              letterSpacing: 0.1,
                              px: 0.8,
                              height: 22,
                              borderRadius: 1.5,
                            }}
                          />
                          {isNew && (
                            <Chip
                              size="small"
                              label=""
                              icon={<Box sx={{ width: 7, height: 7, bgcolor: theme.palette.success.main, borderRadius: '50%' }} />}
                              sx={{
                                background: 'transparent',
                                color: 'success.main',
                                fontWeight: 700,
                                fontSize: '0.88rem',
                                px: 0.1,
                                borderRadius: 1.5,
                                ml: 0.3,
                                minWidth: 0,
                                height: 20,
                              }}
                            />
                          )}
                        </Box>
                        <Typography 
                          gutterBottom 
                          variant="h6" 
                          component="h3"
                          sx={{ 
                            fontWeight: 700,
                            lineHeight: 1.18,
                            mb: 1,
                            minHeight: '2.7rem',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            letterSpacing: 0.01,
                            px: 0,
                            background: 'none',
                            borderRadius: 0,
                            fontSize: '1.13rem',
                          }}
                        >
                          {article.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            mb: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            minHeight: '2.5rem',
                            fontSize: '0.99rem',
                            letterSpacing: 0.01,
                            lineHeight: 1.5,
                          }}
                        >
                          {getCleanPreview(article.content, 180)}
                        </Typography>
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            pt: 0.7,
                            borderTop: `1px solid ${alpha(theme.palette.divider, 0.13)}`,
                            mt: 0.7,
                            marginTop: 'auto',
                          }}
                        >
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              color: 'text.secondary',
                              gap: 0.5,
                              fontSize: '0.95rem',
                            }}
                          >
                            <AccessTimeIcon fontSize="inherit" />
                            {formatDate(article.created_at)}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontWeight: 500,
                              color: 'text.secondary',
                              fontSize: '0.95rem',
                            }}
                          >
                            {article.author.login}
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default Home; 